/**
 * El recuadro de una cita (nombre, teléfono, servicio y acciones).
 *
 * POR QUÉ EXISTE
 * ──────────────
 * Pinchar la caja abre esto: mover y cancelar. La ficha (nombre, teléfono,
 * servicio, hora) se lee ya en el bloque, también en media hora (015 · FR-031).
 */
import { useEffect } from 'react'

import { BotonCancelarCita } from './calendario-acciones'
import { BotonMoverCita } from './calendario-mover'
import type { CitaCalendario } from './calendario-rejilla'

export function DetalleCita({
    accountId,
    dia,
    c,
    nombreProf,
    nombreServ,
    color,
    alCerrar,
}: {
    accountId: string
    dia: string
    c: CitaCalendario
    nombreProf: (id: number | null) => string
    nombreServ: (id: number | null) => string
    color: string
    alCerrar: () => void
}) {
    useEffect(() => {
        const onTecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') alCerrar()
        }
        window.addEventListener('keydown', onTecla)
        return () => window.removeEventListener('keydown', onTecla)
    }, [alCerrar])

    const quien = nombreDe(c)
    const telefono = c.cliente?.telefono?.trim() ?? ''
    const servicio = nombreServ(c.servicioId)
    const profesional = nombreProf(c.profesionalId)
    const cuando = new Date(`${dia}T12:00:00Z`).toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    })

    return (
        <div
            className="fixed inset-0 z-40 flex items-end justify-center overflow-y-auto bg-black/60 p-3 pt-[max(4.5rem,env(safe-area-inset-top))] sm:items-start sm:p-4 sm:pt-20"
            onClick={alCerrar}
            role="presentation"
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="detalle-cita-titulo"
                data-testid="detalle-cita"
                className="mb-[max(0.5rem,env(safe-area-inset-bottom))] max-h-[min(90dvh,40rem)] w-full max-w-lg overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-900 p-4 shadow-xl sm:mb-0"
                onClick={(e) => e.stopPropagation()}
                style={{ borderTopColor: color, borderTopWidth: 3 }}
            >
                <div className="mb-3 min-w-0">
                    <h3 id="detalle-cita-titulo" className="truncate text-base font-medium text-neutral-100">
                        {quien}
                    </h3>
                    <p className="mt-0.5 text-sm capitalize text-neutral-400">{cuando}</p>
                </div>

                {telefono ? (
                    <a
                        href={`tel:${telefono}`}
                        className="mb-3 block font-medium tabular-nums text-sky-300 hover:text-sky-200"
                    >
                        {telefono}
                    </a>
                ) : (
                    <p className="mb-3 text-sm text-neutral-500">Sin teléfono</p>
                )}

                <dl className="space-y-1.5 text-sm">
                    <Fila etiqueta="Hora" valor={`${c.inicio}–${c.fin}`} />
                    {servicio ? <Fila etiqueta="Servicio" valor={servicio} /> : null}
                    {profesional ? <Fila etiqueta="Profesional" valor={profesional} /> : null}
                </dl>

                {c.esTest && (
                    <p className="mt-3 text-xs text-amber-400">La creó el laboratorio de pruebas, no una persona.</p>
                )}
                {c.propuesta && (
                    <p className="mt-3 text-xs text-sky-300">Movida en pantalla: todavía no está escrita en la agenda.</p>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-neutral-800 pt-3">
                    <div className="flex flex-wrap gap-2">
                        <BotonMoverCita
                            accountId={accountId}
                            citaId={c.id}
                            dia={dia}
                            horaInicio={c.inicio}
                            minutos={c.minutos}
                            profesionalId={c.profesionalId}
                            alHecho={alCerrar}
                        />
                        <BotonCancelarCita accountId={accountId} citaId={c.id} alHecho={alCerrar} />
                    </div>
                    <button
                        type="button"
                        onClick={alCerrar}
                        className="shrink-0 rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    )
}

function nombreDe(c: CitaCalendario): string {
    const x = c.cliente
    if (!x) return 'Sin nombre'
    const apellidos = x.apellidos.trim() === '-' ? '' : x.apellidos
    return `${x.nombre} ${apellidos}`.trim() || 'Sin nombre'
}

function Fila({ etiqueta, valor }: { etiqueta: string; valor: string }) {
    return (
        <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-neutral-500">{etiqueta}</dt>
            <dd className="truncate text-right text-neutral-200">{valor}</dd>
        </div>
    )
}
